import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bus, Timer, Users, Award, GitCompare } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import { MechanicTooltip } from "@/components/MechanicTooltip";
import { useVehicleMap, brandOf, vehicleLabel, normPlate } from "@/lib/vehicles";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Comparison() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [routes, setRoutes] = useState([]);
  const vmap = useVehicleMap();
  const [plate, setPlate] = useState("");

  // Karne comparison side-by-side state
  const [mechanicsList, setMechanicsList] = useState([]);
  const [sideA, setSideA] = useState({ mechanicId: "", month: currentMonth(), row: null, pool: null });
  const [sideB, setSideB] = useState({ mechanicId: "", month: currentMonth(), row: null, pool: null });

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/analytics/comparison", { params: plate ? { plate } : {} }),
        api.get("/analytics/routes"),
      ]);
      setRows(c.data || []);
      setRoutes(r.data || []);
    } catch { /* silent — ErrorBoundary dışı sakin kalır */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Load full mechanics list once for the compare selectors
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mechanics");
        const list = data || [];
        setMechanicsList(list);
        if (list.length >= 1) {
          setSideA((s) => ({ ...s, mechanicId: s.mechanicId || list[0].id }));
          setSideB((s) => ({ ...s, mechanicId: s.mechanicId || (list[1]?.id || list[0].id) }));
        }
      } catch { /* silent */ }
    })();
  }, []);

  const loadSide = async (mechanicId, month) => {
    if (!mechanicId || !month) return { row: null, pool: null };
    try {
      // /analytics/pool admin-only; non-admin için atlanır.
      const reqs = [api.get("/analytics/mechanics", { params: { month } })];
      if (isAdmin) reqs.push(api.get("/analytics/pool", { params: { month } }));
      const resList = await Promise.all(reqs);
      const mAll = resList[0];
      const pool = isAdmin ? resList[1] : null;
      const fallbackName = mechanicsList.find((mm) => mm.id === mechanicId)?.name || "—";
      const row = (mAll.data || []).find((x) => x.mechanic_id === mechanicId) || {
        mechanic_id: mechanicId, name: fallbackName,
        total_points: 0, total_tl: 0, job_count: 0, share_percent: 0,
      };
      return { row, pool: pool?.data || null };
    } catch {
      return { row: null, pool: null };
    }
  };

  useEffect(() => {
    if (!sideA.mechanicId) return;
    (async () => {
      const { row, pool } = await loadSide(sideA.mechanicId, sideA.month);
      setSideA((s) => ({ ...s, row, pool }));
    })();
    /* eslint-disable-next-line */
  }, [sideA.mechanicId, sideA.month]);

  useEffect(() => {
    if (!sideB.mechanicId) return;
    (async () => {
      const { row, pool } = await loadSide(sideB.mechanicId, sideB.month);
      setSideB((s) => ({ ...s, row, pool }));
    })();
    /* eslint-disable-next-line */
  }, [sideB.mechanicId, sideB.month]);

  const chartData = useMemo(() => {
    const labelA = sideA.row ? `${sideA.row.name} · ${sideA.month}` : `A · ${sideA.month}`;
    const labelB = sideB.row ? `${sideB.row.name} · ${sideB.month}` : `B · ${sideB.month}`;
    return [
      {
        _side: "A",
        label: labelA,
        name: sideA.row?.name || "—",
        total_points: sideA.row?.total_points || 0,
        total_tl: sideA.row?.total_tl || 0,
        job_count: sideA.row?.job_count || 0,
        share_percent: sideA.row?.share_percent || 0,
      },
      {
        _side: "B",
        label: labelB,
        name: sideB.row?.name || "—",
        total_points: sideB.row?.total_points || 0,
        total_tl: sideB.row?.total_tl || 0,
        job_count: sideB.row?.job_count || 0,
        share_percent: sideB.row?.share_percent || 0,
      },
    ];
  }, [sideA, sideB]);  // The tooltip needs per-side netPool/totalPoints/budget. We pass a function via a wrapper
  // that resolves from the hovered row's _side.
  const CompareTooltip = (props) => {
    if (!props.active || !props.payload || !props.payload.length) return null;
    const r = props.payload[0].payload;
    const side = r._side === "A" ? sideA : sideB;
    return (
      <MechanicTooltip
        active={props.active}
        payload={props.payload}
        netPool={side.pool?.net_pool || 0}
        totalPoints={side.pool?.total_points || 0}
        budget={side.pool?.budget || 0}
        header={`Usta · ${side.month}`}
      />
    );
  };


  return (
    <div className="space-y-6" data-testid="comparison-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Karşılaştırma & Hat Analizi</div>
        <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Servisten Servise Karşılaştırma</h1>
        <p className="mt-2 text-sm text-slate-500">Aynı araç ve aynı arıza için farklı ustaların performansı ve hat bazlı arıza dağılımı.</p>
      </div>

      {/* Karne / Prim yan yana karşılaştırma */}
      <Card className="border-slate-200 shadow-none" data-testid="karne-compare-card">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <GitCompare className="w-4 h-4 text-[#1E3A8A]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">Karne · Yan Yana</div>
                <CardTitle className="font-heading text-lg mt-1">Usta ↔ Usta / Dönem ↔ Dönem Karşılaştırma</CardTitle>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 font-mono">
              Grafiğin üzerine gelince o kolonun anlık puan, pay %, hakediş ve iş sayısı çıkar.
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
          {/* Selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SideSelector
              side="A"
              accent="border-l-[#1E3A8A]"
              color="text-[#1E3A8A]"
              mechanicsList={mechanicsList}
              state={sideA}
              onChange={setSideA}
              testPrefix="side-a"
            />
            <SideSelector
              side="B"
              accent="border-l-emerald-500"
              color="text-emerald-700"
              mechanicsList={mechanicsList}
              state={sideB}
              onChange={setSideB}
              testPrefix="side-b"
            />
          </div>

          {/* Compact summary tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SideSummary state={sideA} tone="navy" isAdmin={isAdmin} />
            <SideSummary state={sideB} tone="emerald" isAdmin={isAdmin} />
          </div>

          {/* Grouped bar chart with rich tooltip */}
          <div className="rounded-md border border-slate-200 p-3 bg-white">
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} height={50} interval={0} />
                <YAxis yAxisId="left"  tick={{ fontSize: 11, fill: "#64748B" }} />
                {isAdmin && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748B" }} />}
                <Tooltip cursor={{ fill: "rgba(30,58,138,0.06)" }} content={<CompareTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="total_points" name="Toplam Puan" radius={[6, 6, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d._side === "A" ? "#1E3A8A" : "#10B981"} />
                  ))}
                </Bar>
                {isAdmin && (
                  <Bar yAxisId="right" dataKey="total_tl" name="Hakediş (TL)" radius={[6, 6, 0, 0]}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d._side === "A" ? "#60A5FA" : "#6EE7B7"} />
                    ))}
                  </Bar>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Delta row */}
          <DeltaRow sideA={sideA} sideB={sideB} isAdmin={isAdmin} />
        </CardContent>
      </Card>

      {/* Route-based */}
      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg">Hat Bazlı Arıza Dağılımı</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">Hat / Rota</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Toplam İş</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Tekrar Arıza</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Toplam Süre (dk)</TableHead>
                  {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-right">Prim (TL)</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {routes.length === 0 && (
                  <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-6 text-slate-400">Henüz veri yok (Filo sayfasından araçların hatlarını girin)</TableCell></TableRow>
                )}
                {routes.map((r) => (
                  <TableRow key={r.route} className="hover:bg-slate-50">
                    <TableCell className="font-semibold">{r.route}</TableCell>
                    <TableCell className="text-right font-mono">{r.jobs}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{r.repeats}</TableCell>
                    <TableCell className="text-right font-mono">{r.total_duration}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right font-mono text-[#1E3A8A] font-semibold">₺{Number(r.total_tl || 0).toLocaleString("tr-TR")}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Comparison */}
      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg">Aynı Arıza — Farklı Usta Karşılaştırması</CardTitle>
            <div className="flex gap-2 items-end">
              <div>
                <Label className="text-xs uppercase tracking-wider">Plaka Filtresi</Label>
                <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="Tüm plakalar" className="mt-1 w-40 font-mono" />
              </div>
              <button onClick={load} className="h-10 px-4 rounded bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#1e40af]" data-testid="comp-filter-btn">Filtrele</button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {rows.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              Bu kriterler için karşılaştırma yok. Aynı plakada aynı arızayı farklı ustalar çözmüş olmalı.
            </div>
          )}
          {rows.map((c, i) => (
            <div key={i} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`comparison-${i}`}>
              <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bus className="w-4 h-4 text-[#1E3A8A]" />
                  <div>
                    <div className="font-heading font-bold text-slate-900">{vehicleLabel(vmap[normPlate(c.plate)]?.code, c.plate, brandOf(vmap, c.plate))}</div>
                    <div className="text-xs text-slate-500">{c.fault}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <Badge variant="outline"><Users className="w-3 h-3 mr-1" /> {c.mechanics_count} usta</Badge>
                  <Badge variant="outline">{c.total_incidents} olay</Badge>
                  {c.duration_diff > 0 && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                      <Timer className="w-3 h-3 mr-1" /> {c.duration_diff} dk fark
                    </Badge>
                  )}
                </div>
              </div>
              <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {c.mechanics.map((m, mi) => (
                  <div
                    key={m.mechanic_id}
                    className={`border rounded-md p-4 ${mi === 0 ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {mi === 0 && <Award className="w-4 h-4 text-emerald-600" />}
                        <span className="font-heading font-bold">{m.mechanic_name}</span>
                      </div>
                      {mi === 0 && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">EN HIZLI</Badge>}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase font-mono">Olay</div>
                        <div className="metric-num text-lg font-bold text-slate-900">{m.count}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase font-mono">Ort. Süre</div>
                        <div className="metric-num text-lg font-bold text-[#1E3A8A]">{m.avg_duration}dk</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-500 uppercase font-mono">Tekrar</div>
                        <div className={`metric-num text-lg font-bold ${m.repeat_count > 0 ? "text-red-600" : "text-emerald-600"}`}>{m.repeat_count}</div>
                      </div>
                    </div>
                    {m.parts.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-[10px] uppercase text-slate-500 font-mono mb-1.5">Kullanılan Parçalar</div>
                        <div className="flex flex-wrap gap-1">
                          {m.parts.map((p, pi) => (
                            <span key={pi} className="text-[11px] px-2 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function SideSelector({ side, accent, color, mechanicsList, state, onChange, testPrefix }) {
  return (
    <div className={`bg-slate-50 border border-slate-200 border-l-4 ${accent} rounded-md p-3 space-y-3`}>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] uppercase tracking-widest font-mono ${color}`}>Taraf {side}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Usta</Label>
          <select
            className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={state.mechanicId}
            onChange={(e) => onChange({ ...state, mechanicId: e.target.value })}
            data-testid={`${testPrefix}-mechanic`}
          >
            {mechanicsList.length === 0 && <option value="">Usta bulunamadı</option>}
            {mechanicsList.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Dönem</Label>
          <Input
            type="month"
            value={state.month}
            onChange={(e) => onChange({ ...state, month: e.target.value })}
            className="mt-1"
            data-testid={`${testPrefix}-month`}
          />
        </div>
      </div>
    </div>
  );
}

function SideSummary({ state, tone, isAdmin }) {
  const r = state.row;
  const pool = state.pool;
  const netPool = pool?.net_pool || 0;
  const totalP = pool?.total_points || 0;
  const points = r?.total_points || 0;
  const sharePct = totalP > 0 ? (points / totalP) * 100 : 0;
  const tl = Math.round(netPool * (totalP > 0 ? points / totalP : 0));
  const isNavy = tone === "navy";
  return (
    <div
      className={`border rounded-md p-3 ${isNavy ? "border-[#1E3A8A]/30 bg-[#1E3A8A]/[0.03]" : "border-emerald-300 bg-emerald-50/40"}`}
    >
      <div className="flex items-baseline justify-between">
        <div className={`font-heading font-bold text-sm ${isNavy ? "text-[#1E3A8A]" : "text-emerald-800"}`}>
          {r?.name || "—"}
        </div>
        <div className="text-[10px] font-mono text-slate-500">{state.month}</div>
      </div>
      <div className={`grid gap-2 mt-2 text-center ${isAdmin ? "grid-cols-4" : "grid-cols-3"}`}>
        <MiniStat label="Puan" value={points.toLocaleString("tr-TR")} />
        {isAdmin && <MiniStat label="Pay %" value={`%${sharePct.toFixed(1)}`} />}
        {isAdmin && <MiniStat label="Hakediş" value={`₺${tl.toLocaleString("tr-TR")}`} strong={isNavy ? "navy" : "emerald"} />}
        <MiniStat label="İş" value={(r?.job_count || 0).toLocaleString("tr-TR")} />
        {!isAdmin && <MiniStat label="Tekrar" value={(r?.repeat_faults || 0).toLocaleString("tr-TR")} />}
      </div>
      {isAdmin && (
        <div className="text-[10px] mt-2 font-mono text-slate-500">
          Havuz: ₺{netPool.toLocaleString("tr-TR")} · Bütçe: ₺{(pool?.budget || 0).toLocaleString("tr-TR")}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, strong }) {
  const color =
    strong === "navy" ? "text-[#1E3A8A]" :
    strong === "emerald" ? "text-emerald-700" :
    "text-slate-900";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`metric-num font-bold text-sm mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}

function DeltaRow({ sideA, sideB, isAdmin }) {
  const rA = sideA.row, rB = sideB.row;
  if (!rA || !rB) return null;
  const pA = sideA.pool, pB = sideB.pool;
  const shareA = pA && pA.total_points > 0 ? (rA.total_points / pA.total_points) * 100 : 0;
  const shareB = pB && pB.total_points > 0 ? (rB.total_points / pB.total_points) * 100 : 0;
  const tlA = pA ? Math.round((pA.net_pool || 0) * (pA.total_points > 0 ? rA.total_points / pA.total_points : 0)) : 0;
  const tlB = pB ? Math.round((pB.net_pool || 0) * (pB.total_points > 0 ? rB.total_points / pB.total_points : 0)) : 0;

  const items = [
    { label: "Puan farkı",    a: rA.total_points, b: rB.total_points, fmt: (v) => v.toLocaleString("tr-TR") },
    ...(isAdmin ? [
      { label: "Pay % farkı",   a: shareA,          b: shareB,          fmt: (v) => `%${v.toFixed(1)}` },
      { label: "Hakediş farkı", a: tlA,             b: tlB,             fmt: (v) => `₺${v.toLocaleString("tr-TR")}` },
    ] : []),
    { label: "İş farkı",      a: rA.job_count,    b: rB.job_count,    fmt: (v) => v.toLocaleString("tr-TR") },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((it, i) => {
        const diff = it.a - it.b;
        const positive = diff >= 0;
        return (
          <div key={i} className="border border-slate-200 rounded-md p-3 bg-white">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{it.label}</div>
            <div className={`metric-num font-bold text-lg mt-1 ${positive ? "text-[#1E3A8A]" : "text-emerald-700"}`}>
              {positive ? "▲" : "▼"} {it.fmt(Math.abs(diff))}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              A: {it.fmt(it.a)} · B: {it.fmt(it.b)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
